import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  getFormFieldRequirements,
  saveFormFieldRequirementsContext
} from "./form-field-requirements.services.ts"
import type {
  FormFieldRequirementGroups,
  FormFieldRequirementsPayload,
  LockedFormFieldOption,
  RequirementGroup
} from "./form-field-requirements.types.ts"
import { readSettingsError } from "../settings-error-envelope.ts"

const EMPTY_GROUPS: FormFieldRequirementGroups = { all: [], any_one: [] }

/** Long enough that dragging a field across two groups is one save rather
 * than three, short enough that leaving the screen straight after a drop
 * still lands inside the flush. */
const AUTOSAVE_DELAY_MS = 700

export type SaveState = "idle" | "pending" | "saving" | "saved" | "error"

const sameGroups = (
  a: FormFieldRequirementGroups,
  b: FormFieldRequirementGroups
) =>
  JSON.stringify(a.all) === JSON.stringify(b.all) &&
  JSON.stringify(a.any_one) === JSON.stringify(b.any_one)

export function useFormFieldRequirements(
  entity: string,
  initialContext?: string
) {
  const [data, setData] = useState<FormFieldRequirementsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [activeContext, setActiveContext] = useState<string | null>(null)
  const [draft, setDraft] = useState<FormFieldRequirementGroups>(EMPTY_GROUPS)
  const [saveState, setSaveState] = useState<SaveState>("idle")
  const [saveError, setSaveError] = useState<string | null>(null)

  // The draft is read back inside event handlers that may fire twice before
  // React re-renders (a fast second drop), so the ref — not the state — is
  // what the next edit and the pending save are computed from.
  const draftRef = useRef<FormFieldRequirementGroups>(EMPTY_GROUPS)
  // Only the newest load may write. Switching entity twice quickly, or a
  // double Retry, otherwise lets the slower earlier request land last and
  // render the entity the user already navigated away from.
  const loadIdRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Holds the call itself, not its arguments: a queued save must go to the
  // entity that was on screen when it was queued, even if the admin has
  // since clicked another one in the sidebar.
  const pendingRef = useRef<(() => void) | null>(null)

  const writeDraft = useCallback((groups: FormFieldRequirementGroups) => {
    draftRef.current = groups
    setDraft(groups)
  }, [])

  const load = useCallback(async () => {
    if (!entity) {
      setLoading(false)
      return
    }
    const loadId = ++loadIdRef.current
    setLoading(true)
    const response = await getFormFieldRequirements(entity)
    if (loadId !== loadIdRef.current) return
    if (response.ok && response.data) {
      const payload = response.data
      const contextKey =
        (initialContext &&
          payload.contexts.some((c) => c.key === initialContext) &&
          initialContext) ||
        payload.contexts[0]?.key ||
        null
      setData(payload)
      setActiveContext(contextKey)
      writeDraft(
        contextKey ? (payload.rules[contextKey] ?? EMPTY_GROUPS) : EMPTY_GROUPS
      )
      setLoadFailed(false)
    } else {
      setLoadFailed(true)
    }
    setLoading(false)
    // `initialContext` deliberately drives only the very first load — once a
    // user picks a context, re-running this on every keystroke-adjacent
    // render would fight their navigation.
  }, [entity, writeDraft])

  useEffect(() => {
    void load()
  }, [load])

  // Every field required by an earlier context in this one's inherits_from
  // chain — shown read-only here, and off-limits to re-add, so the admin
  // only ever configures what's new at this context.
  const inheritedFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    if (!data || !activeContext) return keys
    const byKey = new Map(data.contexts.map((c) => [c.key, c]))
    let current = byKey.get(activeContext)?.inherits_from ?? null
    while (current) {
      const bucket = data.rules[current]
      if (bucket) {
        bucket.all.forEach((k) => keys.add(k))
        bucket.any_one.forEach((k) => keys.add(k))
      }
      current = byKey.get(current)?.inherits_from ?? null
    }
    return keys
  }, [data, activeContext])

  // Locked fields apply from whichever context they are locked at onward —
  // "customer" from the entity's first context (so it is in every chain),
  // "project_type" only from "estimate" up — so unlike inheritedFieldKeys
  // this walks the active context's chain INCLUDING itself, not just its
  // ancestors, and keeps whichever locked fields' own from_context appears
  // in that chain.
  const lockedFieldsForContext = useMemo<LockedFormFieldOption[]>(() => {
    if (!data || !activeContext) return []
    const byKey = new Map(data.contexts.map((c) => [c.key, c]))
    const chain = new Set<string>()
    let current: string | null = activeContext
    while (current) {
      chain.add(current)
      current = byKey.get(current)?.inherits_from ?? null
    }
    return data.locked_fields.filter((f) => chain.has(f.from_context))
  }, [data, activeContext])

  const applyContext = useCallback(
    async (context: string, groups: FormFieldRequirementGroups) => {
      setSaveState("saving")
      setSaveError(null)
      const response = await saveFormFieldRequirementsContext(
        entity,
        context,
        groups.all,
        groups.any_one
      )
      if (response.ok) {
        const payload = response.data?.details?.data
        if (payload) {
          setData(payload)
          // An edit made while this request was in flight is newer than the
          // rows it is answering with; adopting the response here would undo
          // what the user just did and the change would never be sent.
          const saved = payload.rules[context] ?? EMPTY_GROUPS
          if (sameGroups(draftRef.current, groups)) writeDraft(saved)
        }
        setSaveState((state) => (state === "saving" ? "saved" : state))
      } else {
        setSaveError(readSettingsError(response.data))
        setSaveState("error")
      }
      return response.ok
    },
    [entity, writeDraft]
  )

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const pending = pendingRef.current
    pendingRef.current = null
    pending?.()
  }, [])

  const schedule = useCallback(
    (context: string, groups: FormFieldRequirementGroups) => {
      pendingRef.current = () => void applyContext(context, groups)
      setSaveState("pending")
      setSaveError(null)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(flush, AUTOSAVE_DELAY_MS)
    },
    [applyContext, flush]
  )

  // Closing the tab, or navigating away, must not swallow the last edit.
  // `visibilitychange` is the one that can be relied on: a request started in
  // `beforeunload` is routinely cancelled as the document tears down, whereas
  // hiding the tab still leaves the page alive long enough to send it.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") flush()
    }
    const onUnload = () => flush()
    document.addEventListener("visibilitychange", onHide)
    window.addEventListener("beforeunload", onUnload)
    return () => {
      document.removeEventListener("visibilitychange", onHide)
      window.removeEventListener("beforeunload", onUnload)
      flush()
    }
  }, [flush])

  const edit = useCallback(
    (
      updater: (prev: FormFieldRequirementGroups) => FormFieldRequirementGroups
    ) => {
      if (!activeContext) return
      const next = updater(draftRef.current)
      if (sameGroups(next, draftRef.current)) return
      writeDraft(next)
      schedule(activeContext, next)
    },
    [activeContext, schedule, writeDraft]
  )

  const selectContext = useCallback(
    (key: string) => {
      if (!data) return
      flush()
      setActiveContext(key)
      writeDraft(data.rules[key] ?? EMPTY_GROUPS)
      setSaveError(null)
      setSaveState("idle")
    },
    [data, flush, writeDraft]
  )

  const addField = useCallback(
    (group: RequirementGroup, fieldKey: string) =>
      edit((prev) => {
        if (prev.all.includes(fieldKey) || prev.any_one.includes(fieldKey)) {
          return prev
        }
        return { ...prev, [group]: [...prev[group], fieldKey] }
      }),
    [edit]
  )

  const removeField = useCallback(
    (group: RequirementGroup, fieldKey: string) =>
      edit((prev) => ({
        ...prev,
        [group]: prev[group].filter((key) => key !== fieldKey)
      })),
    [edit]
  )

  // Reorders within a group, or moves a field to the other group at a
  // given position — the one operation drag-and-drop needs either way.
  const moveField = useCallback(
    (fieldKey: string, toGroup: RequirementGroup, toIndex: number) =>
      edit((prev) => {
        const withoutField: FormFieldRequirementGroups = {
          all: prev.all.filter((key) => key !== fieldKey),
          any_one: prev.any_one.filter((key) => key !== fieldKey)
        }
        const target = [...withoutField[toGroup]]
        target.splice(toIndex, 0, fieldKey)
        return { ...withoutField, [toGroup]: target }
      }),
    [edit]
  )

  const retrySave = useCallback(() => {
    if (!activeContext) return Promise.resolve(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    pendingRef.current = null
    return applyContext(activeContext, draftRef.current)
  }, [activeContext, applyContext])

  const resetToDefaults = useCallback(() => {
    if (!activeContext) return Promise.resolve(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    pendingRef.current = null
    writeDraft(EMPTY_GROUPS)
    return applyContext(activeContext, EMPTY_GROUPS)
  }, [activeContext, applyContext, writeDraft])

  return {
    data,
    loading,
    loadFailed,
    activeContext,
    draft,
    saveState,
    saveError,
    inheritedFieldKeys,
    lockedFieldsForContext,
    reload: load,
    selectContext,
    flush,
    addField,
    removeField,
    moveField,
    retrySave,
    resetToDefaults
  }
}
